/**
 * Owns the global-search state machine: debounced server-side query against
 * /search, normalized result shape, and a memo'd grouping by type.
 */

import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

export function useGlobalSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Search via server-side endpoint. Replaces the prior client-side approach
  // of fetching every collection and filtering — too heavy for mobile/cellular.
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const debounce = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get("/search", { params: { q: searchQuery, per_type: 5 } });
        const items = (res.data?.results || []).map((r) => ({
          type: r.type,
          id: r.id,
          title: r.title,
          subtitle: r.subtitle,
          status: r.status,
          score: r.score,
          matchedField: r.matched_field,
          matchedValue: r.matched_value,
          matchType: r.match_type,
          data: r.data,
        }));
        setSearchResults(items.slice(0, 20));
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const groupedResults = useMemo(() => {
    return searchResults.reduce((acc, result) => {
      if (!acc[result.type]) acc[result.type] = [];
      acc[result.type].push(result);
      return acc;
    }, {});
  }, [searchResults]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    searching,
    groupedResults,
    resetSearch: () => {
      setSearchQuery("");
      setSearchResults([]);
    },
  };
}
